/* eslint-disable react/no-danger */

import React, { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';
import { ColorNeutralWhite } from 'autogen/design-tokens/tokens';
import type { IExplainedScore } from 'autogen/swagger/all';
import {
    Explanation,
    ParameterName,
    ParameterValue,
    Score,
    ScoreFunction
} from 'autogen/translation-keys/trans-website-explainedscores';
import { zIndex } from 'variables';

const ScoreLabel = styled.div`
    position: absolute;
    top: 25px;
    left: 0;
    z-index: ${zIndex.dialog};
    padding: 3px 6px;
    background-color: #4caf50;
    color: ${ColorNeutralWhite};
    cursor: pointer;
`;

// a one-to-one implementation of explainedScore.cshtml
function ExplainedScoreContent({ item }: { item: IExplainedScore }) {
    const [active, setActive] = useState(false);

    const numOfParameters = Object.keys(item.parameters).length;
    const hasParameters = !!numOfParameters;
    const rowSpan = Math.max(numOfParameters, 1);
    const hasChildrenOrHighlights = !!item.children.length || !!item.highlights.length;

    return (
        <>
            <tr>
                <td rowSpan={rowSpan}>
                    {hasChildrenOrHighlights && (
                        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                    )}
                    {item.name}
                </td>

                <td rowSpan={rowSpan} style={{ fontWeight: item.isMatch ? 'bold' : 'normal' }}>
                    {item.score.toFixed(6)}
                </td>
                <td rowSpan={rowSpan}>{item.explanation}</td>

                {hasParameters ? (
                    <>
                        <td>{Object.keys(item.parameters)[0]}</td>
                        <td>{Object.values(item.parameters)[0] as ReactNode}</td>
                    </>
                ) : (
                    <>
                        <td />
                        <td />
                    </>
                )}
            </tr>

            {Object.entries(item.parameters).map(([key, value], index) => {
                if (index === 0) return null;

                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                        <td>{key}</td>
                        <td>{value.toString()}</td>
                    </tr>
                );
            })}

            {active &&
                item.children.map((child, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <ExplainedScoreContent item={child} key={index + 1} />
                ))}

            {active &&
                item.highlights.map((highlight, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
                        <td colSpan={5} dangerouslySetInnerHTML={{ __html: highlight.replaceAll('\r\n', '<br>') }} />
                    </tr>
                ))}
        </>
    );
}

const StyledTable = styled.table`
    td,
    th {
        padding: 8px;
        border-bottom: 1px solid #b8b8b8;
        width: auto;

        .highlight {
            color: red;
        }
    }
`;

export default function ExplainedScore({ model }: { model: IExplainedScore }) {
    const { t } = useTranslation();

    const content = (
        <StyledTable>
            <thead>
                <tr>
                    <th>{t(ScoreFunction)}</th>
                    <th>{t(Score)}</th>
                    <th>{t(Explanation)}</th>
                    <th>{t(ParameterName)}</th>
                    <th>{t(ParameterValue)}</th>
                </tr>
            </thead>
            <tbody>
                <ExplainedScoreContent item={model} key={0} />
            </tbody>
        </StyledTable>
    );

    return (
        <Tippy
            content={content}
            arrow={false}
            maxWidth="100%"
            interactive
            placement="bottom"
            appendTo={() => document.body}
            animation={false}
        >
            <ScoreLabel>
                {t(Score)} {model.score.toFixed(6)}
            </ScoreLabel>
        </Tippy>
    );
}
